// ============================================================
// usePingGesture - right-click says something, right-drag turns the map.
//
// THE WHOLE DIFFICULTY IS THAT THESE SHARE A BUTTON. Orbit is used
// constantly and must stay instant, so it is NOT delayed waiting to see
// whether a ping was meant: the camera starts orbiting on pointer-down
// exactly as it always has, and this hook watches to see whether it
// should take the gesture back.
//
//   move first          → an orbit. The ping candidate is dropped and the
//                         camera never knows this hook exists.
//   still, then a wait  → a ping. The wheel opens and the camera's drag
//                         is cancelled, so choosing a wedge does not spin
//                         the map.
//   still, then release → a tap. The default ping, no wheel.
//
// A ping is speech, so nothing here writes to a combatant, spends
// movement or touches the turn tracker.
// ============================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import { wheelChoice } from '../engine/battlefield/pings';

/** How long the button must be held, still, before the wheel opens. */
const HOLD_MS = 180;

/** Movement before that which means the gesture was always an orbit. */
export const SLOP_PX = 6;

const travelled = (c, e) => ({ dx: e.clientX - c.x, dy: e.clientY - c.y });

/** Forget the press, and its timer if the wheel has not opened yet. */
function drop(timer, candidate) {
  if (timer.current) clearTimeout(timer.current);
  timer.current = null;
  candidate.current = null;
}

/**
 * What a release says: with the wheel open, the wedge under the pointer -
 * none in the dead centre or outside every wedge, which is how somebody
 * changes their mind, and it must cost nothing; without it, a still
 * release is a tap - the commonest ping by far, which needs no wheel.
 */
function releasedPing(c, { dx, dy }) {
  if (c.open) return wheelChoice(dx, dy);
  return Math.hypot(dx, dy) <= SLOP_PX ? 'look' : null;
}

/**
 * @param onPing        called with ({ type, clientX, clientY }) when one is chosen
 * @param onCancelDrag  the camera's `cancelDrag`, called when the wheel opens
 * @param enabled       false while building, or when the GM has locked pinging
 */
export default function usePingGesture({ onPing, onCancelDrag, enabled = true }) {
  const candidate = useRef(null);
  const timer = useRef(null);
  // Screen-space state for the wheel: where it is, and which wedge is live.
  const [wheel, setWheel] = useState(null);

  const clear = useCallback(() => {
    drop(timer, candidate);
    setWheel(null);
  }, []);

  // A component that unmounts mid-press must not fire a timer afterwards.
  useEffect(() => clear, [clear]);

  const onPointerDown = usePress({ candidate, timer, setWheel, enabled, onCancelDrag });
  const onPointerMove = useWheelTracking(candidate, timer, setWheel);
  const onPointerUp = useRelease(candidate, onPing, clear);
  return { wheel, onPointerDown, onPointerMove, onPointerUp, onPointerCancel: clear };
}

/** A right-button press: a ping candidate, and the timer that opens the wheel if it stays still. */
function usePress({ candidate, timer, setWheel, enabled, onCancelDrag }) {
  return useCallback(
    (e) => {
      if (!enabled || e.button !== 2) return;
      candidate.current = { id: e.pointerId, x: e.clientX, y: e.clientY, open: false };
      timer.current = setTimeout(() => {
        timer.current = null;
        const c = candidate.current;
        if (!c) return;
        c.open = true;
        // The gesture is ours now - stop the camera before the wedge
        // choosing begins, or the map spins under the wheel.
        onCancelDrag?.();
        setWheel({ x: c.x, y: c.y, choice: null });
      }, HOLD_MS);
    },
    [candidate, timer, setWheel, enabled, onCancelDrag],
  );
}

/**
 * A move while pressed. Before the wheel opens, travelling past the slop
 * means this was an orbit all along - the camera has been doing it since
 * pointer-down - so the candidate is dropped. After, it picks the wedge.
 */
function useWheelTracking(candidate, timer, setWheel) {
  return useCallback(
    (e) => {
      const c = candidate.current;
      if (!c || c.id !== e.pointerId) return;
      const { dx, dy } = travelled(c, e);
      if (c.open) setWheel((w) => (w ? { ...w, choice: wheelChoice(dx, dy) } : w));
      else if (Math.hypot(dx, dy) > SLOP_PX) drop(timer, candidate);
    },
    [candidate, timer, setWheel],
  );
}

/** The release: ping what it says (see releasedPing), then forget the gesture. */
function useRelease(candidate, onPing, clear) {
  return useCallback(
    (e) => {
      const c = candidate.current;
      if (!c || c.id !== e.pointerId) return;
      const type = releasedPing(c, travelled(c, e));
      if (type) onPing?.({ type, clientX: c.x, clientY: c.y });
      clear();
    },
    [candidate, onPing, clear],
  );
}
